/**
 * Which version of the app this is, and whether a newer one is live. When you come back to the
 * tab (or every 15 minutes) it checks /version.json; if the site has been updated it offers a
 * reload. Changes you haven't synced yet are kept on this device, so reloading never loses them.
 */
export const APP_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";
export const APP_BUILD = typeof __APP_BUILD__ === "string" ? __APP_BUILD__ : "local";

class VersionWatch {
  /** The newer version that's live, when there is one. */
  available = $state<string | null>(null);
  checking = $state(false);
  lastChecked = $state<string | null>(null);
  private started = false;

  async check(): Promise<void> {
    if (this.checking) return;
    this.checking = true;
    try {
      const r = await fetch("/version.json", { cache: "no-store" });
      if (!r.ok) return;
      const live = (await r.json()) as { version: string; build: string };
      this.lastChecked = new Date().toISOString();
      if (live.build && live.build !== APP_BUILD && APP_BUILD !== "local") {
        this.available =
          live.version === APP_VERSION ? `${live.version} (${live.build})` : live.version;
      }
    } catch {
      /* offline: try again later */
    } finally {
      this.checking = false;
    }
  }

  /**
   * Applies an update without asking when it's safe: nothing waiting to sync, and you're just
   * coming back to the app (so nothing you're looking at jumps). Otherwise the bar asks.
   */
  autoApply(safe: () => boolean) {
    if (typeof document === "undefined") return;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && this.available && safe()) location.reload();
    });
  }

  start() {
    if (this.started || typeof window === "undefined" || !import.meta.env.PROD) return;
    this.started = true;
    void this.check();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void this.check();
    });
    setInterval(() => void this.check(), 15 * 60_000);
  }
}

export const versionWatch = new VersionWatch();
